/**
 * BankingTab Component
 * Stripe Connect setup and processing fee configuration
 */
import { Button } from "../../components/ui/button";
import { 
  CreditCard, Check, Loader2, AlertCircle, Link2, ExternalLink, Unlink
} from "lucide-react";
import useBankingFeePolicy from "./useBankingFeePolicy";
import "./bankingAthletic.css";

const BankingTab = ({ 
  token,
  API,
  user,
  organization,
  setOrganization,
  stripeConnected,
  stripeAccount,
  stripeStatusLoading = false,
  stripeStatusError = false,
  handleRetryStripeStatus,
  connectingStripe,
  disconnectingStripe,
  handleConnectStripe,
  handleDisconnectStripe
}) => {
  const feePolicy = useBankingFeePolicy({API, token, user, setOrganization});
  const handleFeeOptionChange = feePolicy.change;
  const selectedFeePolicy = feePolicy.pending ?? !!organization?.pass_processing_fee;

  const hasCustomFee = organization?.custom_transaction_fee !== null
    && organization?.custom_transaction_fee !== undefined;
  const configuredFee = Number(organization?.custom_transaction_fee);
  const transactionFeePercent = hasCustomFee && Number.isFinite(configuredFee) ? configuredFee : 5;

  const keepMobileFocusVisible = event => {
    if (window.innerWidth > 640) return;
    const rect = event.target.getBoundingClientRect();
    // Native focus scrolling can leave a fractional bottom edge outside the viewport.
    // Correct locally and synchronously; do not alter shell scrolling or privacy UI.
    if (rect.bottom > window.innerHeight) {
      window.scrollBy({ top: Math.ceil(rect.bottom - window.innerHeight) + 8, behavior: 'instant' });
    } else if (rect.top < 0) {
      window.scrollBy({ top: Math.floor(rect.top) - 8, behavior: 'instant' });
    }
  };

  return (
    <section className="banking-athletic" aria-label="Banking & payouts" data-testid="banking-tab" onFocusCapture={keepMobileFocusVisible}>
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Stripe Connect</h3>
            <p className="text-sm text-slate-500 mt-1">
              Connect your Stripe account to receive payments directly from registrations
            </p>
          </div>
          <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-purple-600" />
          </div>
        </div>

        {stripeStatusLoading || stripeStatusError ? (
          <div className="space-y-4">
            {stripeStatusLoading ? (
              <p role="status">Checking Stripe status…</p>
            ) : (
              <>
                <div role="alert">
                  <p className="font-semibold">Status unavailable</p>
                  <p>We could not verify your current Stripe account or payout status. Retry to check again.</p>
                </div>
                <Button className="banking-action bg-purple-600" onClick={handleRetryStripeStatus}>Retry</Button>
              </>
            )}
            {stripeAccount?.id && <div><p>Account ID</p><p className="font-mono text-sm">{stripeAccount.id}</p></div>}
          </div>
        ) : stripeConnected ? (
          <div className="space-y-4">
            {/* Status banner based on account state */}
            {stripeAccount?.charges_enabled ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">Stripe Account Active</p>
                  <p className="text-sm text-green-600">
                    Your account is fully set up and ready to receive payments
                  </p>
                </div>
              </div>
            ) : stripeAccount?.details_submitted ? (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
                </div>
                <div>
                  <p className="font-medium text-yellow-800">Pending Verification</p>
                  <p className="text-sm text-yellow-600">
                    Stripe is reviewing your information. This usually takes 1-2 business days.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-2xl">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-orange-800">Setup Incomplete</p>
                  <p className="text-sm text-orange-600">
                    Please complete your Stripe onboarding to start receiving payments
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-2xl">
                <p className="text-sm text-slate-500">Account Status</p>
                <p className="font-semibold text-slate-900 capitalize">
                  {stripeAccount?.charges_enabled ? "Active" : 
                   stripeAccount?.details_submitted ? "Pending Verification" : "Setup Incomplete"}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-2xl">
                <p className="text-sm text-slate-500">Payouts</p>
                <p className={`font-semibold ${stripeAccount?.payouts_enabled ? 'text-green-600' : 'text-slate-500'}`}>
                  {stripeAccount?.payouts_enabled ? "Enabled" : "Not yet enabled"}
                </p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">Account ID</p>
              <p className="font-mono text-sm text-slate-900">{stripeAccount?.id}</p>
            </div>

            <div className="flex gap-3 flex-wrap">
              {!stripeAccount?.charges_enabled && (
                <Button
                  onClick={handleConnectStripe}
                  disabled={connectingStripe}
                  className="banking-action bg-purple-600 text-white hover:bg-purple-700 rounded-full"
                >
                  {connectingStripe ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Complete Setup
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                className="banking-action border-slate-300"
                onClick={() => window.open("https://connect.stripe.com/express_login", "_blank")}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
              <Button
                variant="outline"
                className="banking-action border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={handleDisconnectStripe}
                disabled={disconnectingStripe}
                data-testid="disconnect-stripe-btn"
              >
                {disconnectingStripe ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect Account
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                Connect your Stripe account to start receiving payments from registrations.
              </p>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl">
              <h4 className="font-medium text-slate-900 mb-2">What you get with Stripe Connect:</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Direct payments to your bank account
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Automatic payout scheduling
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Detailed payment reporting
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Refund management
                </li>
              </ul>
            </div>

            <Button
              onClick={handleConnectStripe}
              disabled={connectingStripe}
              className="banking-action bg-purple-600 text-white hover:bg-purple-700 rounded-full"
            >
              {connectingStripe ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect with Stripe
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Processing Fee Options */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900">Processing Fees</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Choose how to handle transaction fees for registrations
          </p>
          
          {/* Fee Summary */}
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Total Fee Structure</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Sporthouse Transaction Fee</span>
                <span className="font-semibold text-slate-900">{transactionFeePercent}% total</span>
              </div>
              <p className="pt-2 border-t border-slate-300 text-xs text-slate-500">
                Card processing is included. It is not added as a separate fee.
              </p>
            </div>
          </div>
          
          {feePolicy.pending !== null && <p role="status">Saving fee setting… Selection is not yet confirmed.</p>}
          {feePolicy.failed && <div role="alert">
            <p>Could not confirm the fee setting. Showing the last confirmed selection; the server may have changed. Retry to apply your latest choice.</p>
            <Button className="banking-action bg-purple-600" onClick={feePolicy.retry}>Retry fee setting</Button>
          </div>}
          {/* Fee Passing Options */}
          <div className="space-y-3" role="group" aria-label="Processing Fees">
            {/* Option 1: Absorb all fees */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                !selectedFeePolicy
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input type="radio" name="banking-fee-policy" checked={!selectedFeePolicy} onChange={() => handleFeeOptionChange(false)} />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Absorb all fees</p>
                <p className="text-sm text-slate-500">You pay all processing and platform fees</p>
                <p className="text-xs text-slate-400 mt-1">
                  For a $100 registration: User pays $100.00
                </p>
              </div>
            </label>

            {/* Option 2: Pass all fees to user */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedFeePolicy
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input type="radio" name="banking-fee-policy" checked={!!selectedFeePolicy} onChange={() => handleFeeOptionChange(true)} />
              <div className="flex-1">
                <p className="font-medium text-slate-900">Pass all fees to user</p>
                <p className="text-sm text-slate-500">User pays one all-in {transactionFeePercent}% transaction fee</p>
                <p className="text-xs text-slate-400 mt-1">
                  For a $100 registration: User pays ${(100 + transactionFeePercent).toFixed(2)}
                </p>
              </div>
            </label>
          </div>

          {/* Detailed breakdown when passing fees */}
          {organization?.pass_processing_fee && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm font-medium text-blue-900 mb-2">
                Example: $100 Registration Breakdown
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-blue-800">
                  <span>Registration Fee</span>
                  <span>$100.00</span>
                </div>
                <div className="flex justify-between text-blue-800">
                  <span>Transaction Fee ({transactionFeePercent}% total)</span>
                  <span>${transactionFeePercent.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-blue-900 font-semibold pt-1 border-t border-blue-200">
                  <span>Total charged to user</span>
                  <span>${(100 + transactionFeePercent).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BankingTab;
